import { useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import { cn, lockBodyScroll, openLayerCount } from '@eombor/ui';
import { t } from '../i18n/index.js';
import { AuthImage } from './AuthImage.js';
import { GeoLink } from './GeoLink.js';

/**
 * RASMNI KO'RISH oynasi — to'liq ekranda, ilovaning ichida.
 *
 * Ilgari eskiz bosilganda rasm YANGI OYNADA ochilardi va izohda "hujjatni
 * tekshiruvchi uni baribir alohida oynada ko'radi" deb yozilgan edi. Amalda
 * bu ikki sababdan ishlamasdi: havolaga `attachment` yozilgani uchun brauzer
 * rasmni ochmay YUKLAB OLARDI, telefonda esa yangi ilova varag'i ochilib,
 * dalolatnomaga qaytish uchun orqaga bosish kerak bo'lardi.
 *
 * Shuning uchun ko'ruvchi shu yerda: o'q tugmalari bilan rasmlar orasida
 * yuriladi, `Esc` yopadi va ekranning ortida hujjat o'z joyida turadi.
 */

export interface LightboxPhoto {
  id: string;
  /** `files/<uuid>` yoki mock rejimdagi `data:` manzil. */
  url: string;
  gps: { lat: number; lng: number } | null;
  /** Rasm ostidagi qator — dalolatnoma turi va sanasi. */
  caption?: string;
}

export interface PhotoLightboxProps {
  photos: readonly LightboxPhoto[];
  /** Ochiq rasmning tartib raqami; `null` — oyna yopiq. */
  index: number | null;
  onIndexChange: (index: number) => void;
  onClose: () => void;
}

export const PhotoLightbox = ({ photos, index, onIndexChange, onClose }: PhotoLightboxProps) => {
  const closeRef = useRef<HTMLButtonElement>(null);
  const restoreRef = useRef<HTMLElement | null>(null);
  const depthRef = useRef(1);

  const open = index !== null && index >= 0 && index < photos.length;

  /*
   * O'tish AYLANMA: oxirgi rasmdan keyin birinchisi keladi. Ro'yxat qisqa
   * (ko'pi bilan o'nta) va oxiriga yetganda tugma o'chib qolsa, odam
   * "sindi" deb o'ylardi.
   */
  const step = useCallback(
    (delta: number) => {
      if (index === null || photos.length === 0) return;
      onIndexChange((index + delta + photos.length) % photos.length);
    },
    [index, photos.length, onIndexChange],
  );

  useEffect(() => {
    if (!open) return;

    depthRef.current = openLayerCount() + 1;
    const unlock = lockBodyScroll();
    restoreRef.current = document.activeElement as HTMLElement | null;
    closeRef.current?.focus();

    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onClose();
        return;
      }
      if (event.key === 'ArrowRight') step(1);
      if (event.key === 'ArrowLeft') step(-1);
    };

    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      unlock();
      // Fokus avval turgan eskizga qaytadi — klaviatura bilan ishlayotgan
      // odam ro'yxatning boshiga otilib ketmasligi kerak.
      restoreRef.current?.focus();
    };
  }, [open, step, onClose]);

  if (!open) return null;

  const photo = photos[index];
  if (photo === undefined) return null;

  const many = photos.length > 1;

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t('handover.viewerTitle')}
      /*
       * Qatlam `Dialog` BILAN BIR XIL o'lchovda hisoblanadi
       * (`--z-modal` + chuqurlik), chunki ko'ruvchi endi oyna ICHIDAN ham
       * ochiladi: qabul oynasida skladchi bergan suratni kattalashtirish.
       *
       * Ilgari bu yerda `100` yozib qo'yilgan edi — `--z-sticky` ning
       * qiymati. O'sha payt ko'ruvchi faqat sahifadan ochilardi va yetardi,
       * oyna ichidan ochilganda esa rasm `--z-modal` (400) ning ORQASIDA
       * qolib ketardi: ekran qorayadi, rasm ko'rinmaydi.
       */
      style={{ zIndex: `calc(var(--z-modal) + ${(depthRef.current - 1) * 10})` }}
      className="fixed inset-0 flex flex-col bg-black/85 backdrop-blur-sm"
      /* Fon bosilsa yopiladi, rasmning O'ZI bosilsa yo'q: odam rasmni
         kattalashtirmoqchi bo'lib bosishi juda oddiy hol. */
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <header className="flex shrink-0 items-center justify-between gap-3 px-4 py-3 text-white">
        <span className="tabular text-sm font-medium">
          {many ? t('handover.viewerPosition', { index: index + 1, total: photos.length }) : ''}
        </span>
        <button
          ref={closeRef}
          type="button"
          aria-label={t('handover.viewerClose')}
          onClick={onClose}
          className={cn(
            'flex size-10 items-center justify-center rounded-[var(--radius-pill)]',
            'bg-white/10 text-white transition-colors hover:bg-white/20',
            'focus-visible:ring-2 focus-visible:ring-white focus-visible:outline-none',
          )}
        >
          <X size={18} aria-hidden />
        </button>
      </header>

      <div className="flex min-h-0 flex-1 items-center gap-2 px-2 sm:px-4">
        {many ? (
          <NavButton label={t('handover.viewerPrev')} onClick={() => step(-1)}>
            <ChevronLeft size={22} aria-hidden />
          </NavButton>
        ) : null}

        <AuthImage
          /* `key` — rasm ALMASHGANDA komponent qaytadan quriladi va eskisi
             o'z `objectURL` ini bo'shatadi. Usiz bitta `<img>` ichida
             manzillar almashib, bo'shatish kechikardi. */
          key={photo.id}
          src={photo.url}
          alt={photo.caption ?? t('handover.viewerTitle')}
          className="mx-auto max-h-full max-w-full rounded-[var(--radius-md)] object-contain"
          skeletonClassName="h-64 w-64"
        />

        {many ? (
          <NavButton label={t('handover.viewerNext')} onClick={() => step(1)}>
            <ChevronRight size={22} aria-hidden />
          </NavButton>
        ) : null}
      </div>

      <footer className="flex shrink-0 flex-wrap items-center justify-center gap-x-4 gap-y-1 px-4 py-3 text-sm text-white/80">
        {photo.caption === undefined ? null : <span>{photo.caption}</span>}
        {/* Koordinata rasm bilan BIRGA ko'rinadi: "bu qayerda olingan"
            degan savol aynan rasmga qarab turganda tug'iladi. */}
        {photo.gps === null ? null : <GeoLink gps={photo.gps} tone="onDark" />}
      </footer>
    </div>,
    document.body,
  );
};

const NavButton = ({
  label,
  onClick,
  children,
}: {
  label: string;
  onClick: () => void;
  children: React.ReactNode;
}) => (
  <button
    type="button"
    aria-label={label}
    onClick={onClick}
    className={cn(
      'flex size-11 shrink-0 items-center justify-center rounded-[var(--radius-pill)]',
      'bg-white/10 text-white transition-colors hover:bg-white/20',
      'focus-visible:ring-2 focus-visible:ring-white focus-visible:outline-none',
    )}
  >
    {children}
  </button>
);
